<!-- Settings Page - Mobile First with Tile Modals -->
<div class="container py-4">
  <!-- Toast Notification -->
  <div *ngIf="showToast" class="custom-toast" [ngClass]="toastType">
    <i
      class="me-2"
      [class.bi-check-circle-fill]="toastType === 'success'"
      [class.bi-exclamation-circle-fill]="toastType === 'error'"
    ></i>
    {{ toastMessage }}
  </div>

  <!-- Welcome Header -->
  <div
    class="card bg-linear-gradient-purple text-white mb-4 border-0 shadow-sm"
  >
    <div class="card-body py-4 text-center">
      <div class="user-avatar mb-3">
        <i class="bi bi-person-circle" style="font-size: 4rem"></i>
      </div>
      <h2 class="mb-1 fw-bold">{{ getDisplayName() }}</h2>
      <p class="mb-0 opacity-75">Manage your profile settings</p>
    </div>
  </div>

  <!-- Account Setup Progress Banner -->
  <div
    *ngIf="!loading && !setupComplete"
    class="alert alert-warning border-0 shadow-sm mb-4"
    role="alert"
  >
    <div class="d-flex align-items-start">
      <div class="flex-shrink-0">
        <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i>
      </div>
      <div class="flex-grow-1">
        <h5 class="alert-heading mb-2">
          <i class="bi bi-shield-exclamation me-2"></i>Complete Your Account
          Setup
        </h5>
        <p class="mb-2">
          Setting up your email and password enables important features and
          keeps your account secure.
        </p>

        <!-- Progress Bar -->
        <div class="progress mb-3" style="height: 8px">
          <div
            class="progress-bar"
            [class.bg-danger]="setupScore < 33"
            [class.bg-warning]="setupScore >= 33 && setupScore < 66"
            [class.bg-success]="setupScore >= 66"
            role="progressbar"
            [style.width.%]="setupScore"
            [attr.aria-valuenow]="setupScore"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>

        <!-- Benefits List -->
        <div class="row g-2 mb-3">
          <div class="col-md-6">
            <div class="d-flex align-items-center">
              <i
                class="bi me-2"
                [class.bi-check-circle-fill]="hasEmail"
                [class.text-success]="hasEmail"
                [class.bi-circle]="!hasEmail"
                [class.text-muted]="!hasEmail"
              ></i>
              <small
                ><strong>Email notifications</strong> for schedule
                updates</small
              >
            </div>
          </div>
          <div class="col-md-6">
            <div class="d-flex align-items-center">
              <i
                class="bi me-2"
                [class.bi-check-circle-fill]="hasPassword"
                [class.text-success]="hasPassword"
                [class.bi-circle]="!hasPassword"
                [class.text-muted]="!hasPassword"
              ></i>
              <small
                ><strong>Password recovery</strong> if you forget PIN</small
              >
            </div>
          </div>
          <div class="col-md-6">
            <div class="d-flex align-items-center">
              <i
                class="bi me-2"
                [class.bi-check-circle-fill]="hasEmail && hasPassword"
                [class.text-success]="hasEmail && hasPassword"
                [class.bi-circle]="!hasEmail || !hasPassword"
                [class.text-muted]="!hasEmail || !hasPassword"
              ></i>
              <small
                ><strong>Alerts</strong> for shift swap and time-off
                requests</small
              >
            </div>
          </div>
          <div class="col-md-6">
            <div class="d-flex align-items-center">
              <i
                class="bi me-2"
                [class.bi-check-circle-fill]="hasEmail && hasPassword"
                [class.text-success]="hasEmail && hasPassword"
                [class.bi-circle]="!hasEmail || !hasPassword"
                [class.text-muted]="!hasEmail || !hasPassword"
              ></i>
              <small
                ><strong>Secure access</strong> with multiple login
                options</small
              >
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex flex-wrap gap-2">
          <button
            *ngIf="!hasEmail"
            class="btn btn-warning btn-sm"
            (click)="openModal('personal')"
          >
            <i class="bi bi-envelope me-1"></i>Add Email
          </button>
          <button
            *ngIf="!hasPassword"
            class="btn btn-warning btn-sm"
            (click)="openModal('security')"
          >
            <i class="bi bi-key me-1"></i>Set Password
          </button>
          <button
            *ngIf="!hasPin"
            class="btn btn-warning btn-sm"
            (click)="openModal('security')"
          >
            <i class="bi bi-lock me-1"></i>Set PIN
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Success Banner (when setup is complete) -->
  <div
    *ngIf="!loading && setupComplete"
    class="alert alert-success border-0 shadow-sm mb-4"
    role="alert"
  >
    <div class="d-flex align-items-center">
      <i class="bi bi-check-circle-fill fs-4 me-3"></i>
      <div>
        <h6 class="alert-heading mb-1">
          <i class="bi bi-shield-check me-2"></i>Account Fully Secured
        </h6>
        <p class="mb-0">
          <small
            >Your email, password, and PIN are set up. You're ready to receive
            notifications!</small
          >
        </p>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="text-center py-5">
    <div
      class="spinner-border text-primary mb-3"
      role="status"
      style="width: 3rem; height: 3rem"
    >
      <span class="visually-hidden">Loading...</span>
    </div>
    <p class="text-muted">Loading your profile...</p>
  </div>

  <!-- Settings Tiles -->
  <div *ngIf="!loading" class="settings-grid">
    <!-- Personal Info Tile -->
    <div class="settings-tile" (click)="openModal('personal')">
      <div class="tile-icon bg-gradient-purple">
        <i class="bi bi-person-badge"></i>
      </div>
      <div class="tile-content">
        <h5 class="tile-title">
          Personal Information
          <span
            *ngIf="!hasEmail"
            class="badge bg-warning text-dark ms-2"
            style="font-size: 0.7rem"
          >
            <i class="bi bi-exclamation-circle me-1"></i>Action Needed
          </span>
        </h5>
        <p class="tile-description">Name and email address</p>
        <div class="tile-preview">
          <small class="text-muted">
            <i
              class="bi me-1"
              [class.bi-envelope]="hasEmail"
              [class.bi-envelope-slash]="!hasEmail"
            ></i>
            {{ user?.email || "No email set" }}
          </small>
        </div>
      </div>
      <i class="bi bi-chevron-right tile-arrow"></i>
    </div>

    <!-- Security Tile -->
    <div class="settings-tile" (click)="openModal('security')">
      <div class="tile-icon bg-gradient-purple">
        <i class="bi bi-shield-lock"></i>
      </div>
      <div class="tile-content">
        <h5 class="tile-title">
          Security Settings
          <span
            *ngIf="!hasPassword || !hasPin"
            class="badge bg-warning text-dark ms-2"
            style="font-size: 0.7rem"
          >
            <i class="bi bi-exclamation-circle me-1"></i>Action Needed
          </span>
        </h5>
        <p class="tile-description">PIN and password</p>
        <div class="tile-preview">
          <div class="d-flex flex-column gap-1">
            <small class="text-muted">
              <i
                class="bi me-1"
                [class.bi-lock-fill]="hasPin"
                [class.bi-unlock]="!hasPin"
              ></i>
              PIN: {{ user?.Pin ? "••••" : "Not set" }}
            </small>
            <small class="text-muted">
              <i
                class="bi me-1"
                [class.bi-key-fill]="hasPassword"
                [class.bi-key]="!hasPassword"
              ></i>
              Password: {{ hasPassword ? "••••••••" : "Not set" }}
            </small>
          </div>
        </div>
      </div>
      <i class="bi bi-chevron-right tile-arrow"></i>
    </div>

    <!-- Support Tile -->
    <div class="settings-tile" (click)="openModal('support')">
      <div class="tile-icon bg-gradient-purple">
        <i class="bi bi-headset"></i>
      </div>
      <div class="tile-content">
        <h5 class="tile-title">Support</h5>
        <p class="tile-description">Submit a ticket or get help</p>
      </div>
      <i class="bi bi-chevron-right tile-arrow"></i>
    </div>

    <!-- Support Modal -->
    <app-ticket-system></app-ticket-system>

    <div class="settings-tile" (click)="navigateToDocumentation()">
      <div class="tile-icon bg-gradient-purple">
        <i class="bi bi-book"></i>
      </div>
      <div class="tile-content">
        <h5 class="tile-title">Documentation</h5>
        <p class="tile-description">Learn how to use the app</p>
        <div class="tile-preview">
          <small class="text-muted">
            <i class="bi bi-journal-text me-1"></i>
            Step-by-step guides and FAQs
          </small>
        </div>
      </div>
      <i class="bi bi-chevron-right tile-arrow"></i>
    </div>
  </div>

  <!-- Personal Information Modal -->
  <div
    class="modal fade"
    id="personalModal"
    tabindex="-1"
    aria-labelledby="personalModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content border-0 shadow-lg">
        <div class="modal-header bg-linear-gradient-purple text-white border-0">
          <h5 class="modal-title" id="personalModalLabel">
            <i class="bi bi-person-badge me-2"></i>Personal Information
          </h5>
          <button
            type="button"
            class="btn-close btn-close-white"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <!-- Email Benefits Alert -->
          <div
            *ngIf="!hasEmail"
            class="alert alert-info border-0 mb-4"
            role="alert"
          >
            <div class="d-flex">
              <i class="bi bi-lightbulb-fill fs-5 me-3 flex-shrink-0"></i>
              <div>
                <h6 class="alert-heading mb-2">Why add your email?</h6>
                <ul class="mb-0 ps-3" style="font-size: 0.9rem">
                  <li>Get weekly schedule updates delivered to your inbox</li>
                  <li>Receive notifications on shift swap requests</li>
                  <li>Get alerts when your time-off is approved/denied</li>
                  <li>Recover your account if you forget your PIN</li>
                </ul>
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Username</label>
            <div class="input-group">
              <span class="input-group-text bg-light border-end-0">
                <i class="bi bi-person-badge text-muted"></i>
              </span>
              <input
                type="text"
                class="form-control border-start-0 ps-0 bg-light"
                [value]="user?.Name || getDisplayName()"
                readonly
              />
            </div>
            <small class="text-muted">
              <i class="bi bi-info-circle me-1"></i>
              This is your system username. If you need it changed, please
              submit a support ticket — an Admin can update it for you.
            </small>
          </div>
          <form [formGroup]="form">
            <div class="mb-3">
              <label class="form-label fw-semibold">
                First Name <span class="text-danger">*</span>
              </label>
              <div class="input-group">
                <span class="input-group-text bg-light border-end-0">
                  <i class="bi bi-person text-muted"></i>
                </span>
                <input
                  type="text"
                  class="form-control border-start-0 ps-0"
                  formControlName="firstName"
                  placeholder="John"
                  [class.is-invalid]="
                    form.get('firstName')?.invalid &&
                    form.get('firstName')?.touched
                  "
                />
              </div>
              <div
                class="text-danger small mt-1"
                *ngIf="
                  form.get('firstName')?.invalid &&
                  form.get('firstName')?.touched
                "
              >
                First name is required
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label fw-semibold">
                Last Name <span class="text-danger">*</span>
              </label>
              <div class="input-group">
                <span class="input-group-text bg-light border-end-0">
                  <i class="bi bi-person text-muted"></i>
                </span>
                <input
                  type="text"
                  class="form-control border-start-0 ps-0"
                  formControlName="lastName"
                  placeholder="Doe"
                  [class.is-invalid]="
                    form.get('lastName')?.invalid &&
                    form.get('lastName')?.touched
                  "
                />
              </div>
              <div
                class="text-danger small mt-1"
                *ngIf="
                  form.get('lastName')?.invalid && form.get('lastName')?.touched
                "
              >
                Last name is required
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label fw-semibold">
                Email <span class="text-danger">*</span>
                <span
                  *ngIf="!hasEmail"
                  class="badge bg-warning text-dark ms-2"
                  style="font-size: 0.7rem"
                >
                  Required for notifications
                </span>
              </label>
              <div class="input-group">
                <span class="input-group-text bg-light border-end-0">
                  <i class="bi bi-envelope text-muted"></i>
                </span>
                <input
                  type="email"
                  class="form-control border-start-0 ps-0"
                  formControlName="email"
                  placeholder="john.doe@example.com"
                  [class.is-invalid]="
                    form.get('email')?.invalid && form.get('email')?.touched
                  "
                />
              </div>
              <div
                class="text-danger small mt-1"
                *ngIf="form.get('email')?.invalid && form.get('email')?.touched"
              >
                <span *ngIf="form.get('email')?.errors?.['required']">
                  Email is required
                </span>
                <span *ngIf="form.get('email')?.errors?.['email']">
                  Please enter a valid email
                </span>
              </div>
              <small class="text-muted">
                <i class="bi bi-shield-check me-1"></i>
                Used for login and notifications. We'll never share your email.
              </small>
            </div>
          </form>
        </div>
        <div class="modal-footer border-0 bg-light">
          <button
            type="button"
            class="btn btn-outline-secondary"
            data-bs-dismiss="modal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-gradient"
            (click)="savePersonalInfo()"
            [disabled]="
              saving ||
              form.get('firstName')?.invalid ||
              form.get('lastName')?.invalid ||
              form.get('email')?.invalid
            "
          >
            <span *ngIf="!saving">
              <i class="bi bi-check-circle me-2"></i>Save Changes
            </span>
            <span *ngIf="saving">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Saving...
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Security Modal -->
  <div
    class="modal fade"
    id="securityModal"
    tabindex="-1"
    aria-labelledby="securityModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content border-0 shadow-lg">
        <div class="modal-header bg-linear-gradient-purple text-white border-0">
          <h5 class="modal-title" id="securityModalLabel">
            <i class="bi bi-shield-lock me-2"></i>Security Settings
          </h5>
          <button
            type="button"
            class="btn-close btn-close-white"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <!-- Password Benefits Alert -->
          <div
            *ngIf="!hasPassword"
            class="alert alert-warning border-0 mb-4"
            role="alert"
          >
            <div class="d-flex">
              <i class="bi bi-shield-exclamation fs-5 me-3 flex-shrink-0"></i>
              <div>
                <h6 class="alert-heading mb-2">
                  <i class="bi bi-key-fill me-2"></i>Set up a password for
                  account recovery
                </h6>
                <p class="mb-2" style="font-size: 0.9rem">
                  If you forget your PIN, you can log in with your email and
                  password instead. This ensures you never lose access to your
                  account.
                </p>
                <small>
                  <strong>Tip:</strong> Use a unique password that you don't use
                  elsewhere.
                </small>
              </div>
            </div>
          </div>

          <form [formGroup]="form">
            <div class="mb-3">
              <label class="form-label fw-semibold">
                Security PIN <span class="text-danger">*</span>
              </label>
              <div class="input-group">
                <span class="input-group-text bg-light border-end-0">
                  <i class="bi bi-lock text-muted"></i>
                </span>
                <input
                  [type]="showPin ? 'text' : 'password'"
                  class="form-control border-start-0 border-end-0 ps-0"
                  formControlName="pin"
                  placeholder="Enter 4-6 digit PIN"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  maxlength="6"
                  [class.is-invalid]="
                    form.get('pin')?.invalid && form.get('pin')?.touched
                  "
                />
                <button
                  class="btn btn-outline-secondary border-start-0"
                  type="button"
                  (click)="toggleShowPin()"
                >
                  <i [class]="showPin ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                </button>
              </div>
              <div
                class="text-danger small mt-1"
                *ngIf="form.get('pin')?.invalid && form.get('pin')?.touched"
              >
                <span *ngIf="form.get('pin')?.errors?.['required']">
                  PIN is required
                </span>
                <span *ngIf="form.get('pin')?.errors?.['minlength']">
                  PIN must be at least 4 digits
                </span>
                <span *ngIf="form.get('pin')?.errors?.['maxlength']">
                  PIN must be no more than 6 digits
                </span>
                <span *ngIf="form.get('pin')?.errors?.['pattern']">
                  PIN must contain only numbers
                </span>
              </div>
              <small class="text-muted">
                <i class="bi bi-info-circle me-1"></i>
                4-6 digits for quick clock-in
              </small>
            </div>

            <div class="mb-3">
              <label class="form-label fw-semibold">
                Password
                <span
                  *ngIf="!hasPassword"
                  class="badge bg-warning text-dark ms-2"
                  style="font-size: 0.7rem"
                >
                  Highly Recommended
                </span>
              </label>
              <div class="input-group">
                <span class="input-group-text bg-light border-end-0">
                  <i class="bi bi-key text-muted"></i>
                </span>
                <input
                  [type]="showPassword ? 'text' : 'password'"
                  class="form-control border-start-0 border-end-0 ps-0"
                  formControlName="newPassword"
                  [placeholder]="
                    hasPassword
                      ? 'Enter new password to change'
                      : 'Create a secure password'
                  "
                  [class.is-invalid]="
                    form.get('newPassword')?.invalid &&
                    form.get('newPassword')?.touched
                  "
                />
                <button
                  class="btn btn-outline-secondary border-start-0"
                  type="button"
                  (click)="toggleShowPassword()"
                >
                  <i
                    [class]="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"
                  ></i>
                </button>
              </div>
              <div
                class="text-danger small mt-1"
                *ngIf="
                  form.get('newPassword')?.invalid &&
                  form.get('newPassword')?.touched
                "
              >
                <span *ngIf="form.get('newPassword')?.errors?.['minlength']">
                  Password must be at least 6 characters
                </span>
              </div>
              <small class="text-muted">
                <i class="bi bi-shield-check me-1"></i>
                <span *ngIf="!hasPassword">
                  Set a password to enable account recovery and alternative
                  login
                </span>
                <span *ngIf="hasPassword">
                  Leave blank to keep current password. Passwords are securely
                  encrypted.
                </span>
              </small>
            </div>

            <!-- Security Features Info -->
            <div class="alert alert-light border mb-0">
              <h6 class="mb-2">
                <i class="bi bi-info-circle me-2"></i>Login Options
              </h6>
              <ul class="mb-0 ps-3" style="font-size: 0.85rem">
                <li>
                  <strong>Quick access:</strong> Use your PIN for fast clock-in
                </li>
                <li>
                  <strong>Full access:</strong> Use email + password for all
                  features
                </li>
                <li>
                  <strong>Forgot PIN?</strong> Reset via email if password is
                  set
                </li>
              </ul>
            </div>
          </form>
        </div>
        <div class="modal-footer border-0 bg-light">
          <button
            type="button"
            class="btn btn-outline-secondary"
            data-bs-dismiss="modal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-gradient"
            (click)="saveSecurityInfo()"
            [disabled]="saving || form.get('pin')?.invalid"
          >
            <span *ngIf="!saving">
              <i class="bi bi-check-circle me-2"></i>Save Changes
            </span>
            <span *ngIf="saving">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Saving...
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
